import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Button, Modal, SearchableDropdown } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  Address,
  AddressPayload,
  addressesAPI,
} from '@/services/api/addresses';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';

import AddressEndpointFields, {
  EndpointField,
  EndpointValues,
} from './AddressEndpointFields';
import AddressPickerMap, { PickTarget } from './AddressPickerMap';

interface AddressFormModalProps {
  isOpen: boolean;
  /** Null = create mode. An address = edit mode. */
  address: Address | null;
  onClose: () => void;
  onSaved: (address: Address) => void;
}

interface FormState {
  clientId: string;
  originName: string;
  originAddress: string;
  originLat: string;
  originLong: string;
  destinationName: string;
  destinationAddress: string;
  destinationLat: string;
  destinationLong: string;
  distance: string;
}

const EMPTY: FormState = {
  clientId: '',
  originName: '',
  originAddress: '',
  originLat: '',
  originLong: '',
  destinationName: '',
  destinationAddress: '',
  destinationLat: '',
  destinationLong: '',
  distance: '',
};

// Maps the endpoint sub-component's generic fields onto the flat FormState keys.
type EndpointMap = Record<EndpointField, keyof FormState>;
const ORIGIN_MAP: EndpointMap = {
  name: 'originName',
  address: 'originAddress',
  lat: 'originLat',
  long: 'originLong',
};
const DEST_MAP: EndpointMap = {
  name: 'destinationName',
  address: 'destinationAddress',
  lat: 'destinationLat',
  long: 'destinationLong',
};

const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
const coordCls = `${inputCls} font-mono`;

const parseCoord = (v: string): number | null => {
  const n = Number(v);
  return v.trim() !== '' && Number.isFinite(n) ? n : null;
};

const AddressFormModal: React.FC<AddressFormModalProps> = ({
  isOpen,
  address,
  onClose,
  onSaved,
}) => {
  const isEdit = address !== null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>(
    {}
  );
  const [active, setActive] = useState<PickTarget>('origin');

  // Owning client/provider picker (lane-level clientId). Server-searchable.
  const providers = useProvidersDropdown(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setSubmitting(false);
    setErrors({});
    setActive('origin');
    if (address) {
      setForm({
        clientId: address.clientId != null ? String(address.clientId) : '',
        originName: address.originName,
        originAddress: address.originAddress,
        originLat: String(address.originLat),
        originLong: String(address.originLong),
        destinationName: address.destinationName,
        destinationAddress: address.destinationAddress,
        destinationLat: String(address.destinationLat),
        destinationLong: String(address.destinationLong),
        distance: String(address.distance),
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, address]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const originPoint = useMemo(() => {
    const lat = parseCoord(form.originLat);
    const lng = parseCoord(form.originLong);
    return lat !== null && lng !== null ? { lat, lng } : null;
  }, [form.originLat, form.originLong]);

  const destPoint = useMemo(() => {
    const lat = parseCoord(form.destinationLat);
    const lng = parseCoord(form.destinationLong);
    return lat !== null && lng !== null ? { lat, lng } : null;
  }, [form.destinationLat, form.destinationLong]);

  // Google Maps Directions link (origin → destination) so ops can eyeball the
  // driving distance and copy it into the Jarak field. Null until both set.
  const gmapsDirUrl = useMemo(() => {
    if (!originPoint || !destPoint) return null;
    return `https://www.google.com/maps/dir/?api=1&origin=${originPoint.lat},${originPoint.lng}&destination=${destPoint.lat},${destPoint.lng}&travelmode=driving`;
  }, [originPoint, destPoint]);

  // Bridge the flat FormState to the endpoint sub-component's field shape.
  const endpointValues = (map: EndpointMap): EndpointValues => ({
    name: form[map.name],
    address: form[map.address],
    lat: form[map.lat],
    long: form[map.long],
  });
  const endpointErrors = (map: EndpointMap) => ({
    name: errors[map.name],
    address: errors[map.address],
    lat: errors[map.lat],
    long: errors[map.long],
  });
  const onEndpointField =
    (map: EndpointMap) => (field: EndpointField, value: string) =>
      setField(map[field], value);
  const onEndpointPatch =
    (map: EndpointMap) => (patch: Partial<EndpointValues>) => {
      setForm((prev) => {
        const next = { ...prev };
        (Object.entries(patch) as [EndpointField, string][]).forEach(
          ([k, v]) => {
            next[map[k]] = v;
          }
        );
        return next;
      });
      setErrors((prev) => {
        const next = { ...prev };
        (Object.keys(patch) as EndpointField[]).forEach((k) => {
          delete next[map[k]];
        });
        return next;
      });
    };

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.clientId.trim() || Number(form.clientId) <= 0)
      e.clientId = 'Wajib pilih client';
    if (!form.originName.trim()) e.originName = 'Wajib diisi';
    if (!form.originAddress.trim()) e.originAddress = 'Wajib diisi';
    if (!form.destinationName.trim()) e.destinationName = 'Wajib diisi';
    if (!form.destinationAddress.trim()) e.destinationAddress = 'Wajib diisi';

    const oLat = parseCoord(form.originLat);
    const oLng = parseCoord(form.originLong);
    const dLat = parseCoord(form.destinationLat);
    const dLng = parseCoord(form.destinationLong);
    if (oLat === null || oLat < -90 || oLat > 90) e.originLat = 'Lat -90..90';
    if (oLng === null || oLng < -180 || oLng > 180) e.originLong = 'Long -180..180';
    if (dLat === null || dLat < -90 || dLat > 90) e.destinationLat = 'Lat -90..90';
    if (dLng === null || dLng < -180 || dLng > 180)
      e.destinationLong = 'Long -180..180';

    const dist = Number(form.distance);
    if (form.distance.trim() === '' || !Number.isFinite(dist) || dist < 0)
      e.distance = 'Jarak (m) ≥ 0';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload: AddressPayload = {
        clientId: Number(form.clientId),
        originName: form.originName.trim(),
        originAddress: form.originAddress.trim(),
        origin: {
          latitude: Number(form.originLat),
          longitude: Number(form.originLong),
        },
        destinationName: form.destinationName.trim(),
        destinationAddress: form.destinationAddress.trim(),
        destination: {
          latitude: Number(form.destinationLat),
          longitude: Number(form.destinationLong),
        },
        // meters, integer
        distance: Math.round(Number(form.distance)),
      };
      const saved =
        isEdit && address
          ? await addressesAPI.update(address.id, payload)
          : await addressesAPI.create(payload);
      showSuccessToast(
        isEdit ? 'Address diperbarui' : 'Address dibuat',
        `${saved.originName} → ${saved.destinationName}`
      );
      onSaved(saved);
      onClose();
    } catch (err) {
      showErrorToast(
        isEdit ? 'Gagal memperbarui address' : 'Gagal membuat address',
        err instanceof Error ? err.message : 'Unknown error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      icon={isEdit ? 'ri-edit-line' : 'ri-add-line'}
      iconTone="primary"
      title={isEdit ? 'Edit address lane' : 'Address lane baru'}
      subtitle={
        isEdit && address
          ? `${address.originName} → ${address.destinationName}`
          : 'Lane origin → destination + jarak. Dipakai juga sebagai cache geocode.'
      }
      width="full"
      confirmOnClose
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? 'Menyimpan…'
              : isEdit
              ? 'Simpan perubahan'
              : 'Buat lane'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Owning client/provider for this lane (lane-level clientId). */}
        <div>
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
            Client <span className="text-red-500">*</span>
            <span className="ml-2 font-normal normal-case tracking-normal text-gray-400">
              provider/client pemilik lane
            </span>
          </label>
          <div className="max-w-md">
            <SearchableDropdown
              options={providers.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={form.clientId}
              selectedLabelFallback={
                address?.client?.name ?? providers.labelFor(form.clientId)
              }
              onChange={(next) => setField('clientId', String(next))}
              onSearchChange={providers.setSearch}
              disableLocalFilter
              loading={providers.loading}
              hasMore={providers.hasMore}
              onLoadMore={providers.loadMore}
              placeholder="Cari client / provider…"
              emptyMessage={providers.error ?? 'Tidak ada client'}
            />
          </div>
          {errors.clientId && (
            <p className="mt-1 text-[11px] text-red-600">{errors.clientId}</p>
          )}
        </div>

        {/* Origin & destination — each picks its source independently:
            hub · provider client · custom address. All three fill the same
            name/address/lat/long fields. */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <AddressEndpointFields
            role="origin"
            label="Origin"
            color="#5E2AAC"
            open={isOpen}
            values={endpointValues(ORIGIN_MAP)}
            errors={endpointErrors(ORIGIN_MAP)}
            onFieldChange={onEndpointField(ORIGIN_MAP)}
            onPatch={onEndpointPatch(ORIGIN_MAP)}
          />
          <AddressEndpointFields
            role="destination"
            label="Destination"
            color="#2563eb"
            open={isOpen}
            values={endpointValues(DEST_MAP)}
            errors={endpointErrors(DEST_MAP)}
            onFieldChange={onEndpointField(DEST_MAP)}
            onPatch={onEndpointPatch(DEST_MAP)}
          />
        </div>

        {/* Map picker for both points — sits below origin/destination. */}
        <AddressPickerMap
          origin={originPoint}
          destination={destPoint}
          active={active}
          onActiveChange={setActive}
          height={360}
          onOriginChange={(lat, lng) =>
            setForm((prev) => ({
              ...prev,
              originLat: String(lat),
              originLong: String(lng),
            }))
          }
          onDestinationChange={(lat, lng) =>
            setForm((prev) => ({
              ...prev,
              destinationLat: String(lat),
              destinationLong: String(lng),
            }))
          }
        />

        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor="addr-distance"
              className="block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500"
            >
              Jarak (m) <span className="text-red-500">*</span>
              <span className="ml-2 font-normal normal-case tracking-normal text-gray-400">
                cek jarak tempuh di Google Maps, isi dalam meter
              </span>
            </label>
            {gmapsDirUrl ? (
              <a
                href={gmapsDirUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-gsm border border-gray-300 px-2.5 py-1.5 text-[12px] font-semibold tracking-tight text-primary transition-colors hover:border-primary hover:bg-[rgba(94,42,172,0.05)]"
              >
                <i className="ri-route-line text-[14px]" aria-hidden="true" />
                Buka rute di Google Maps
                <i
                  className="ri-external-link-line text-[12px]"
                  aria-hidden="true"
                />
              </a>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-gsm border border-gray-200 px-2.5 py-1.5 text-[12px] font-medium tracking-tight text-gray-400">
                <i className="ri-route-line text-[14px]" aria-hidden="true" />
                Set origin &amp; destination dulu untuk buka rute
              </span>
            )}
          </div>
          <input
            id="addr-distance"
            type="number"
            step="1"
            min={0}
            value={form.distance}
            onChange={(e) => setField('distance', e.target.value)}
            placeholder="8400"
            className={`${coordCls} max-w-[220px]`}
          />
          {errors.distance && (
            <p className="mt-1 text-[11px] text-red-600">{errors.distance}</p>
          )}
        </div>

        {isEdit && (
          <p className="text-[11px] text-gray-400">
            <Badge tone="neutral" size="small" className="mr-2">
              H3 index
            </Badge>
            Di-derive otomatis server-side dari koordinat.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default AddressFormModal;
